import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Loader2, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

const ManagerLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user, role, isLoading, signIn } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in as manager
  useEffect(() => {
    if (!isLoading && user && role === 'manager') {
      navigate('/manager-dashboard');
    }
  }, [user, role, isLoading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Missing Credentials",
        description: "Please enter both email and password.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        toast({
          title: "Login Failed",
          description: error.message || "Invalid email or password.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // The useEffect will check role and redirect
      toast({
        title: "Login Successful",
        description: "Verifying access...",
      });
    } catch (error: any) {
      toast({
        title: "Login Failed",
        description: error.message || "An error occurred during login.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  // Show error if logged in but not a manager
  useEffect(() => {
    if (!isLoading && user && role && role !== 'manager') {
      toast({
        title: "Access Denied",
        description: "You don't have manager access. Please use the partner login.",
        variant: "destructive",
      });
      navigate('/partner-login');
    }
  }, [user, role, isLoading, navigate, toast]);

  if (isLoading) {
    return (
      <PageLayout>
        <main className="pt-20 flex-grow flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-delphi-red" />
        </main>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <main className="pt-20 flex-grow">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto">
            <Card className="border border-delphi-gray/20 bg-delphi-dark/80 backdrop-blur-sm shadow-lg">
              <CardHeader className="space-y-1 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-delphi-red/20 flex items-center justify-center mb-2">
                  <Shield className="w-6 h-6 text-delphi-red" />
                </div>
                <CardTitle className="text-2xl text-delphi-light">Manager Login</CardTitle>
                <CardDescription className="text-delphi-light/70">
                  Authorized personnel only
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-delphi-light">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-delphi-light">
                      Password
                    </label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                      disabled={isSubmitting}
                    />
                  </div>
                </form>
              </CardContent>
              <CardFooter className="flex-col space-y-4">
                <Button 
                  className="w-full bg-delphi-red hover:bg-red-600"
                  onClick={handleLogin}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    <>
                      Access Dashboard
                      <ArrowRight size={16} className="ml-2" />
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
            <p className="text-center mt-4 text-delphi-light/50 text-xs">
              Manager accounts are created by system administrators only.
            </p>
          </div>
        </div>
      </main>
    </PageLayout>
  );
};

export default ManagerLogin;
